import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Plus, Search, FolderOpen, Clock, RefreshCw, Download, CheckSquare, Users, ArrowUpDown, UserPlus, Settings, Eye, Pencil, Trash2, LayoutGrid, List as ListIcon, Car, Target
} from 'lucide-react';

const CASE_TYPES = [
  { value: 'CHILD_CUSTODY', label: 'Child Custody' },
  { value: 'INFIDELITY', label: 'Infidelity' },
  { value: 'MISSING_PERSON', label: 'Missing Person' },
  { value: 'BAIL_BOND_APPREHENSION', label: 'Bail Bond Apprehension' },
  { value: 'WORKERS_COMP', label: 'Workers Compensation' },
  { value: 'STOLEN_PROPERTY', label: 'Stolen Property' },
  { value: 'DOMESTIC_VIOLENCE', label: 'Domestic Violence' },
  { value: 'BACKGROUND_CHECK', label: 'Background Check' },
  { value: 'SKIP_TRACE', label: 'Skip Trace' },
  { value: 'INSURANCE_FRAUD', label: 'Insurance Fraud' },
  { value: 'ASSET_SEARCH', label: 'Asset Search' },
  { value: 'SURVEILLANCE', label: 'Surveillance' },
  { value: 'OTHER', label: 'Other' },
];

const CASE_STATUSES = [
  { value: 'NEW', label: 'New' }, { value: 'PENDING_REVIEW', label: 'Pending Review' },
  { value: 'ASSIGNED', label: 'Assigned' }, { value: 'ACTIVE', label: 'Active' },
  { value: 'EVIDENCE_COLLECTION', label: 'Evidence Collection' },
  { value: 'AWAITING_CLIENT_INFO', label: 'Awaiting Client Info' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'COMPLETED', label: 'Completed' }, { value: 'CLOSED', label: 'Closed' },
];

const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-700', PENDING_REVIEW: 'bg-yellow-100 text-yellow-700',
  ASSIGNED: 'bg-purple-100 text-purple-700', ACTIVE: 'bg-green-100 text-green-700',
  EVIDENCE_COLLECTION: 'bg-cyan-100 text-cyan-700', AWAITING_CLIENT_INFO: 'bg-orange-100 text-orange-700',
  UNDER_REVIEW: 'bg-indigo-100 text-indigo-700', COMPLETED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-slate-100 text-slate-600'
};

const Cases = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false); // legacy (no longer used; full-page /cases/new now)
  const [newCase, setNewCase] = useState({ title: '', case_type: 'SURVEILLANCE', client_id: '', description: '', priority: 'NORMAL' });
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [bulkAssignUser, setBulkAssignUser] = useState('');
  const [bulkAssignRole, setBulkAssignRole] = useState('field_investigator');
  const [bulkNewStatus, setBulkNewStatus] = useState('ACTIVE');
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('cases_view_mode') || 'list');
  useEffect(() => { localStorage.setItem('cases_view_mode', viewMode); }, [viewMode]);
  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';

  // Fetch company investigators for bulk assign
  const fetchCompanyUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/investigators`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCompanyUsers(await res.json());
    } catch (e) { console.error(e); }
  }, [token, isAdmin]);

  useEffect(() => { if (isAdmin) fetchCompanyUsers(); }, [fetchCompanyUsers, isAdmin]);

  // Toggle case selection
  const toggleCaseSelection = (caseId) => {
    setSelectedCases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(caseId)) {
        newSet.delete(caseId);
      } else {
        newSet.add(caseId);
      }
      return newSet;
    });
  };

  // Select/deselect all filtered cases
  const toggleSelectAll = () => {
    if (selectedCases.size === filteredCases.length) {
      setSelectedCases(new Set());
    } else {
      setSelectedCases(new Set(filteredCases.map(c => c.id)));
    }
  };

  // Export cases to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `nexustrac_cases_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast({ title: 'Success', description: 'Cases exported to CSV' });
      } else {
        throw new Error('Export failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  // Bulk assign cases
  const handleBulkAssign = async () => {
    if (!bulkAssignUser) {
      toast({ title: 'Error', description: 'Please select a user', variant: 'destructive' });
      return;
    }
    if (selectedCases.size === 0) {
      toast({ title: 'Error', description: 'No cases selected', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/bulk-assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          case_ids: Array.from(selectedCases),
          user_id: bulkAssignUser,
          assignment_role: bulkAssignRole
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: 'Success', description: data.message });
        setShowBulkAssign(false);
        setBulkAssignUser('');
        setBulkAssignRole('field_investigator');
        setSelectedCases(new Set());
        fetchCases();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Bulk assign failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Bulk status change
  const handleBulkStatusChange = async () => {
    if (selectedCases.size === 0) {
      toast({ title: 'Error', description: 'No cases selected', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/bulk-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          case_ids: Array.from(selectedCases),
          new_status: bulkNewStatus
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: 'Success', description: data.message });
        setShowBulkStatus(false);
        setBulkNewStatus('ACTIVE');
        setSelectedCases(new Set());
        fetchCases();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Bulk status change failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (typeFilter !== 'ALL') params.set('case_type', typeFilter);
      const res = await fetch(`${BACKEND_URL}/api/cases?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCases(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, statusFilter, typeFilter]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchCases(); fetchClients(); }, [fetchCases, fetchClients]);

  const handleCreateCase = async () => {
    if (!newCase.title || !newCase.client_id) {
      toast({ title: 'Error', description: 'Title and Client are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newCase)
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: 'Success', description: `Case ${data.case_number} created` });
        setShowCreate(false);
        setNewCase({ title: '', case_type: 'SURVEILLANCE', client_id: '', description: '', priority: 'NORMAL' });
        fetchCases();
        navigate(`/cases/${data.id}`);
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create case');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const filteredCases = cases.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.title?.toLowerCase().includes(s) || c.case_number?.toLowerCase().includes(s) || c.description?.toLowerCase().includes(s);
  });

  // Edit/Delete state for case rows
  const [editingCase, setEditingCase] = useState(null);
  const [editCaseForm, setEditCaseForm] = useState({ title: '', case_type: '', priority: '', description: '' });
  const [savingCaseEdit, setSavingCaseEdit] = useState(false);

  const openEditCase = (c) => {
    setEditingCase(c);
    setEditCaseForm({
      title: c.title || '',
      case_type: c.case_type || 'SURVEILLANCE',
      priority: c.priority || 'NORMAL',
      description: c.description || ''
    });
  };

  const saveEditCase = async () => {
    if (!editingCase) return;
    if (!editCaseForm.title?.trim()) {
      toast({ title: 'Error', description: 'Title is required', variant: 'destructive' });
      return;
    }
    setSavingCaseEdit(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${editingCase.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editCaseForm)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Update failed (${res.status})`);
      }
      toast({ title: 'Case updated', description: editCaseForm.title });
      setEditingCase(null);
      fetchCases();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingCaseEdit(false);
    }
  };

  const handleDeleteCase = async (c) => {
    if (!window.confirm(`Delete case ${c.case_number} (${c.title})?\n\nThis will permanently remove the case and all related records.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${c.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Delete failed (${res.status})`);
      }
      toast({ title: 'Case deleted', description: c.case_number });
      fetchCases();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-5 max-w-6xl w-full overflow-x-hidden" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="nx-eyebrow">Case Management</div>
          <h1 className="nx-h1 mt-1">Investigation Cases</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Export Button */}
          <Button 
            variant="outline" 
            className="border-slate-300 text-slate-600" 
            onClick={handleExportCSV}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>

          {/* Bulk Actions (Admin only) */}
          {isAdmin && selectedCases.size > 0 && (
            <>
              <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="bulk-assign-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Assign ({selectedCases.size})
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Bulk Assign Cases</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-slate-600">Assign <strong>{selectedCases.size}</strong> selected case(s) to an investigator.</p>
                    <div>
                      <Label>Select User</Label>
                      <Select value={bulkAssignUser} onValueChange={setBulkAssignUser}>
                        <SelectTrigger className="border-slate-300" data-testid="bulk-assign-user-select">
                          <SelectValue placeholder="Choose investigator..." />
                        </SelectTrigger>
                        <SelectContent>
                          {companyUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Assignment Role</Label>
                      <Select value={bulkAssignRole} onValueChange={setBulkAssignRole}>
                        <SelectTrigger className="border-slate-300" data-testid="bulk-assign-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Iter121H — expanded command roles (match single-assign dialog). */}
                          <SelectItem value="chief">Chief</SelectItem>
                          <SelectItem value="assistant_chief">Assistant Chief</SelectItem>
                          <SelectItem value="captain">Captain</SelectItem>
                          <SelectItem value="lieutenant">Lieutenant</SelectItem>
                          <SelectItem value="intelligence_commander">Intelligence Commander</SelectItem>
                          <SelectItem value="field_sergeant">Field Sergeant</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="lead_investigator">Lead Investigator</SelectItem>
                          <SelectItem value="field_investigator">Field Investigator</SelectItem>
                          <SelectItem value="support_investigator">Support Investigator</SelectItem>
                          <SelectItem value="skip_tracer">Skip Tracer</SelectItem>
                          <SelectItem value="transporter">Transporter</SelectItem>
                          <SelectItem value="analyst">Analyst</SelectItem>
                          <SelectItem value="case_manager">Case Manager</SelectItem>
                          <SelectItem value="trainee">Trainee</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleBulkAssign} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="confirm-bulk-assign-btn">
                      Assign to Selected Cases
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={showBulkStatus} onOpenChange={setShowBulkStatus}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-amber-300 text-amber-600 hover:bg-amber-50" data-testid="bulk-status-btn">
                    <Settings className="w-4 h-4 mr-2" /> Status ({selectedCases.size})
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Bulk Change Status</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-slate-600">Change status of <strong>{selectedCases.size}</strong> selected case(s).</p>
                    <div>
                      <Label>New Status</Label>
                      <Select value={bulkNewStatus} onValueChange={setBulkNewStatus}>
                        <SelectTrigger className="border-slate-300" data-testid="bulk-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CASE_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleBulkStatusChange} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="confirm-bulk-status-btn">
                      Update Status
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}

          <Button variant="outline" className="border-slate-300 text-slate-600" onClick={fetchCases}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {/* Iter136 — List / Grid wanted-poster view toggle */}
          <div className="inline-flex items-stretch rounded-md border border-slate-300 overflow-hidden" data-testid="cases-view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1 ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="cases-view-list-btn"
            >
              <ListIcon className="w-3 h-3" /> List
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="cases-view-grid-btn"
            >
              <LayoutGrid className="w-3 h-3" /> Grid
            </button>
          </div>
          {user?.role !== 'client' && (
            <>
              <Button 
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white" 
                onClick={() => navigate('/cases/new')}
                data-testid="create-case-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Case
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              className="border-slate-300 pl-10" placeholder="Search cases..." data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="border-slate-300"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {CASE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="border-slate-300"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-slate-300 text-slate-500" onClick={() => { setSearch(''); setStatusFilter('ALL'); setTypeFilter('ALL'); }}>
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Case Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading cases...</div>
      ) : filteredCases.length === 0 ? (
        <div className="bg-slate-900/50 border border-slate-700 rounded-sm p-12 text-center">
          <FolderOpen className="w-12 h-12 mx-auto text-slate-500 mb-3" />
          <h3 className="font-heading font-semibold text-slate-200 mb-1">No Cases Found</h3>
          <p className="text-sm text-slate-500">
            {search || statusFilter !== 'ALL' || typeFilter !== 'ALL' ? 'Try adjusting your filters' : 'Create your first investigation case'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <WantedPosterGrid cases={filteredCases} navigate={navigate} />
      ) : (
        <div className="bg-slate-900/50 border border-slate-700 rounded-sm overflow-hidden">
          {/* Selection Info Bar */}
          {isAdmin && selectedCases.size > 0 && (
            <div className="bg-blue-50 border-b border-blue-200 px-5 py-2 flex items-center justify-between">
              <span className="text-sm text-blue-700">
                <CheckSquare className="w-4 h-4 inline mr-2" />
                {selectedCases.size} case(s) selected
              </span>
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800" onClick={() => setSelectedCases(new Set())} data-testid="clear-selection-btn">
                Clear Selection
              </Button>
            </div>
          )}
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-slate-800/60 border-b border-slate-700 text-[10px] font-mono text-slate-400 uppercase tracking-[0.15em]">
            {isAdmin && (
              <div className="col-span-1 flex items-center">
                <Checkbox 
                  checked={selectedCases.size === filteredCases.length && filteredCases.length > 0}
                  onCheckedChange={toggleSelectAll}
                  className="border-slate-300"
                  data-testid="select-all-checkbox"
                />
              </div>
            )}
            <div className={isAdmin ? "col-span-2" : "col-span-2"}>Case #</div>
            <div className={isAdmin ? "col-span-2" : "col-span-3"}>Title</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filteredCases.map((c, i) => (
            <div 
              key={c.id}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3.5 hover:bg-slate-800/50 transition-colors items-center ${i < filteredCases.length - 1 ? 'border-b border-slate-700' : ''} ${selectedCases.has(c.id) ? 'bg-blue-900/30' : ''}`}
              data-testid={`case-row-${c.id}`}
            >
              {isAdmin && (
                <div className="col-span-1 flex items-center" onClick={(e) => e.stopPropagation()}>
                  <Checkbox 
                    checked={selectedCases.has(c.id)}
                    onCheckedChange={() => toggleCaseSelection(c.id)}
                    className="border-slate-300"
                    data-testid={`case-checkbox-${c.id}`}
                  />
                </div>
              )}
              <Link to={`/cases/${c.id}`} className={`${isAdmin ? 'col-span-2' : 'col-span-2'} font-mono text-xs text-slate-400 hover:text-slate-200 truncate`} data-testid={`case-link-${c.id}`}>
                {c.case_number}
              </Link>
              <Link to={`/cases/${c.id}`} className={isAdmin ? "col-span-2" : "col-span-3"}>
                <div className="flex items-center gap-2">
                  {c.photo_url && (
                    <img
                      src={c.photo_url}
                      alt=""
                      className="w-8 h-8 rounded object-cover border border-slate-700 flex-shrink-0"
                      data-testid={`case-photo-thumb-${c.id}`}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-100 hover:text-amber-300 truncate">{c.title}</p>
                    {c.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{c.description}</p>}
                  </div>
                </div>
              </Link>
              <div className="col-span-2 text-xs text-slate-400">{c.case_type?.replace(/_/g, ' ')}</div>
              <div className="col-span-2">
                <Badge className={`${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-600'} text-[10px] font-medium rounded-sm`}>
                  {c.status?.replace(/_/g, ' ')}
                </Badge>
                {(c.priority === 'URGENT' || c.priority === 'HIGH') && (
                  <Badge className={`ml-1 ${c.priority === 'URGENT' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'} text-[10px] rounded-sm`}>
                    {c.priority}
                  </Badge>
                )}
              </div>
              <div className="col-span-1 flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                {new Date(c.created_at).toLocaleDateString()}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <Button
                  variant="ghost" size="sm" className="h-8 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  onClick={() => navigate(`/cases/${c.id}`)}
                  data-testid={`case-view-${c.id}`}
                  title="View case"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                {(isAdmin || user?.role !== 'client') && (
                  <Button
                    variant="ghost" size="sm" className="h-8 px-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                    onClick={() => openEditCase(c)}
                    data-testid={`case-edit-${c.id}`}
                    title="Edit case"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="ghost" size="sm" className="h-8 px-2 text-slate-600 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteCase(c)}
                    data-testid={`case-delete-${c.id}`}
                    title="Delete case"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Case Dialog */}
      <Dialog open={!!editingCase} onOpenChange={(open) => { if (!open) setEditingCase(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="edit-case-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Case {editingCase?.case_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={editCaseForm.title}
                onChange={e => setEditCaseForm({ ...editCaseForm, title: e.target.value })}
                className="border-slate-300"
                data-testid="edit-case-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Case Type</Label>
                <Select value={editCaseForm.case_type} onValueChange={v => setEditCaseForm({ ...editCaseForm, case_type: v })}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={editCaseForm.priority} onValueChange={v => setEditCaseForm({ ...editCaseForm, priority: v })}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editCaseForm.description}
                onChange={e => setEditCaseForm({ ...editCaseForm, description: e.target.value })}
                className="border-slate-300"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-slate-300 text-slate-700" onClick={() => setEditingCase(null)}>Cancel</Button>
              <Button
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white disabled:opacity-60"
                onClick={saveEditCase}
                disabled={savingCaseEdit || !editCaseForm.title?.trim()}
                data-testid="save-edit-case-btn"
              >
                {savingCaseEdit ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cases;

/* Iter136 — Category-aware wanted-poster grid.  Vehicle-recovery cases get
   a REPOSSESSION card, bail-bond fugitive cases get a WANTED card, every
   other category gets a neutral case-file card.  No fake data — this
   reads whatever subject/vehicle photo is already on the case record. */
function posterTypeFor(c) {
  const t = (c?.case_type || '').toUpperCase();
  if (['STOLEN_PROPERTY'].includes(t)) return 'REPOSSESSION';
  if (['BAIL_BOND_APPREHENSION', 'MISSING_PERSON'].includes(t)) return 'WANTED';
  if (['SKIP_TRACE', 'DOMESTIC_VIOLENCE', 'INFIDELITY', 'CHILD_CUSTODY'].includes(t)) return 'SURVEILLANCE';
  return 'CASE FILE';
}
function posterPaletteFor(kind) {
  switch (kind) {
    case 'WANTED':       return { bar: 'bg-red-700',    tag: 'bg-red-100 text-red-800 border-red-300' };
    case 'REPOSSESSION': return { bar: 'bg-amber-600',  tag: 'bg-amber-100 text-amber-800 border-amber-300' };
    case 'SURVEILLANCE': return { bar: 'bg-blue-700',   tag: 'bg-blue-100 text-blue-800 border-blue-300' };
    default:             return { bar: 'bg-slate-700',  tag: 'bg-slate-100 text-slate-800 border-slate-300' };
  }
}
function WantedPosterGrid({ cases, navigate }) {
  if (!cases.length) return null;
  const prioTag = (p) => {
    const v = (p || 'NORMAL').toUpperCase();
    const map = {
      URGENT: 'bg-red-600 text-white border-red-700',
      HIGH:   'bg-orange-500 text-white border-orange-600',
      NORMAL: 'bg-slate-700 text-white border-slate-800',
      LOW:    'bg-slate-200 text-slate-800 border-slate-400',
    };
    return map[v] || map.NORMAL;
  };
  const statusTag = (s) => {
    const v = (s || '').toUpperCase();
    if (v.includes('CLOSED') || v.includes('COMPLETE')) return 'bg-emerald-600 text-white border-emerald-700';
    if (v.includes('PENDING') || v.includes('AWAITING')) return 'bg-amber-500 text-slate-900 border-amber-600';
    if (v.includes('ACTIVE') || v.includes('OPEN') || v.includes('INVESTIGATION')) return 'bg-blue-600 text-white border-blue-700';
    return 'bg-slate-700 text-white border-slate-800';
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="cases-wanted-grid">
      {cases.map(c => {
        const kind = posterTypeFor(c);
        const palette = posterPaletteFor(kind);
        const img = c.photo_url || c.subject_photo_url || c.vehicle_photo_url;
        const label = c.title || c.case_number;
        const desc = (c.description || '').trim();
        const initial = (c.initial_notes || c.notes || c.starting_notes || '').trim();
        return (
          <button
            key={c.id}
            onClick={() => navigate(`/cases/${c.id}`)}
            className="group text-left bg-[#F8F5EC] text-slate-900 rounded-md border-2 border-slate-900 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition overflow-hidden flex flex-col"
            data-testid={`case-poster-${c.id}`}
          >
            <div className={`${palette.bar} text-white text-center py-2`}>
              <div className="text-[10px] font-mono tracking-[0.3em] opacity-80">NEXUSTRAC</div>
              <div className="text-2xl font-black tracking-[0.25em] leading-none">{kind}</div>
            </div>
            <div className="aspect-[4/5] bg-slate-200 border-y-2 border-slate-900 flex items-center justify-center overflow-hidden relative">
              {img ? (
                <img src={img} alt={label} className="w-full h-full object-cover" />
              ) : kind === 'REPOSSESSION' ? (
                <Car className="w-16 h-16 text-slate-500" />
              ) : (
                <Target className="w-16 h-16 text-slate-500" />
              )}
            </div>
            {/* Iter146 — High-contrast info panel with description + initial notes */}
            <div className="bg-white text-slate-900 p-3 space-y-2 flex-1 flex flex-col">
              <div className="font-serif text-lg font-bold leading-tight line-clamp-2" data-testid={`case-name-${c.id}`}>{label}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono tracking-wider text-slate-700 uppercase bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5">
                  {c.case_number}
                </span>
                <span className="text-[10px] font-mono tracking-wider text-slate-600 uppercase">
                  {c.case_type?.replace(/_/g, ' ') || 'Case'}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center text-[11px] font-mono font-semibold px-2.5 py-1 rounded border ${prioTag(c.priority)}`}
                      data-testid={`case-priority-${c.id}`}>
                  {(c.priority || 'NORMAL').toUpperCase()}
                </span>
                <span className={`inline-flex items-center text-[11px] font-mono font-semibold px-2.5 py-1 rounded border uppercase ${statusTag(c.status)}`}
                      data-testid={`case-status-${c.id}`}>
                  {(c.status || '').replace(/_/g, ' ') || 'STATUS'}
                </span>
              </div>
              <div className="pt-1 border-t border-slate-200">
                <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider mb-0.5">Description</div>
                <p className="text-[12px] leading-snug text-slate-800 line-clamp-3">
                  {desc || <span className="italic text-slate-500">No description provided</span>}
                </p>
              </div>
              {initial && (
                <div>
                  <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider mb-0.5">Initial Notes</div>
                  <p className="text-[12px] leading-snug text-slate-700 line-clamp-2">{initial}</p>
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
